import axios from "../axiosConfig";
import { getSocket } from "./socket";

const POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 15 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

const sleep = (ms) => new Promise(resolve => {
  window.setTimeout(resolve, ms);
});

const emitProgress = (onProgress, job) => {
  if (typeof onProgress === "function" && job) onProgress(job);
};

const createJobError = job => {
  const error = new Error(
    job?.error?.message || job?.errorMessage ||
    "LOADEvo Intelligence could not analyse the document."
  );
  error.code = job?.error?.code || job?.errorCode || "LOAD_INTELLIGENCE_IMPORT_FAILED";
  error.importJobId = job?.id || job?.jobId || null;
  error.job = job || null;
  return error;
};

export const getLoadIntelligenceProgressLabel = job => {
  const stage = String(job?.stage || "").trim().toUpperCase();
  const status = String(job?.status || "").trim().toUpperCase();

  if (status === "FAILED") return "Analysis failed";
  if (status === "COMPLETED") {
    return job?.requiresReview ? "Fields ready — review required" : "Fields ready";
  }

  switch (stage) {
    case "UPLOAD_RECEIVED": return "Document received";
    case "PROCESSING":
    case "DOCUMENT_ANALYSIS": return "Preparing document";
    case "INTELLIGENCE_PROCESSING": return "Reading and validating document";
    case "LOCATION_RESOLUTION": return "Document ready — verifying locations";
    case "RECOVERED": return "Resuming document analysis";
    default: return "Analysing document";
  }
};

export async function importTransportOrderAsync(formData, { onProgress, onDraftReady } = {}) {
  let socket = null;
  let socketHandler = null;
  let importJobId = null;
  let latestJob = null;
  let preliminaryDelivered = false;

  try {
    const startResponse = await axios.post(
      "/loads/intelligence/import-v5-async", formData,
      { headers: { "Content-Type": undefined }, timeout: 30000 }
    );

    importJobId = startResponse?.data?.importJobId ||
      startResponse?.data?.jobId || startResponse?.data?.job?.id;

    if (!importJobId) {
      throw new Error("The backend did not return an AI import job id.");
    }

    latestJob = startResponse?.data?.job || {
      id: importJobId,
      status: "QUEUED",
      stage: "UPLOAD_RECEIVED",
      progress: null,
    };
    emitProgress(onProgress, latestJob);

    // Socket.IO is the fast path for progress only; polling remains authoritative.
    try {
      socket = getSocket();
      if (socket && typeof socket.on === "function") {
        socketHandler = payload => {
          const payloadJobId = payload?.id || payload?.jobId;
          if (String(payloadJobId || "") !== String(importJobId)) return;
          latestJob = { ...(latestJob || {}), ...payload };
          emitProgress(onProgress, latestJob);
        };
        socket.on("load-intelligence:job", socketHandler);
      }
    } catch (socketError) {
      console.warn("[load-intelligence] Socket progress unavailable; polling remains active.", socketError);
    }

    const deadline = Date.now() + MAX_WAIT_MS;
    let consecutivePollErrors = 0;

    while (Date.now() < deadline) {
      try {
        const statusResponse = await axios.get(
          `/loads/intelligence/import-jobs/${encodeURIComponent(importJobId)}`,
          { timeout: 15000 }
        );
        consecutivePollErrors = 0;

        const returnedJob = statusResponse?.data?.job || statusResponse?.data || latestJob;
        latestJob = {
          ...(returnedJob || {}),
          requiresReview: returnedJob?.requiresReview === true ||
            returnedJob?.result?.requiresReview === true,
        };
        emitProgress(onProgress, latestJob);

        // Only the authenticated owner-scoped GET can contain a preliminary draft.
        // Fire once; never replay a preliminary result over the user's edits.
        if (!preliminaryDelivered && latestJob?.preliminaryResult?.draft &&
            typeof onDraftReady === "function") {
          onDraftReady(latestJob.preliminaryResult, latestJob);
          preliminaryDelivered = true;
        }

        if (latestJob?.status === "COMPLETED") {
          if (!latestJob.result || typeof latestJob.result !== "object") {
            throw new Error("The completed AI import contains no usable result.");
          }
          return { data: latestJob.result, importJobId, job: latestJob };
        }

        if (latestJob?.status === "FAILED") throw createJobError(latestJob);
      } catch (error) {
        if (error?.job?.status === "FAILED") throw error;
        const status = error?.response?.status;
        if (status && status < 500) throw error;
        consecutivePollErrors += 1;
        if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) throw error;
      }
      await sleep(POLL_INTERVAL_MS);
    }

    const timeoutError = new Error("Document analysis is taking longer than expected.");
    timeoutError.code = "LOAD_INTELLIGENCE_WAIT_TIMEOUT";
    timeoutError.importJobId = importJobId;
    throw timeoutError;
  } finally {
    if (socket && socketHandler && typeof socket.off === "function") {
      socket.off("load-intelligence:job", socketHandler);
    }
  }
}
